import React, { useState } from 'react'
import { Select, Tooltip } from 'antd'
import {
  EduButton,
  captureSentryException,
  notification,
} from '@edulastic/common'
import { isEmpty } from 'lodash'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { IconEditMetaData, IconInfo } from '@edulastic/icons'

import {
  StyledApplyButton,
  StyledDivider,
  StyledEmptyStateContainer,
  StyledEmptyStateText,
  StyledErrorText,
  StyledGradientLoader,
  StyledLoadingContainer,
  StyledMetadataItem,
  StyledMetadataLabel,
  StyledMetadataLabelContainer,
  StyledMetadataSection,
  StyledRecommendationItem,
  StyledRecommendationLabel,
  StyledRecommendationList,
  StyledRecommendationListItem,
  StyledRecommendationsContainer,
  StyledRecommendationText,
  StyledSelect,
  StyledShowSuggestionsButton,
  StyledSpinnerWrapper,
  StyledStandardContainer,
  StyledStandardDescription,
  StyledStandardEditContainer,
  StyledStandardIdentifier,
  StyledStandardIdentifierContainer,
  StyledThinkingText,
  StyledStandardTooltipContainer,
  StyledStandardTooltipLabel,
  StyledStandardTooltipRow,
} from './styled'
import { getCurrentQuestionSelector } from '../../sharedDucks/questions'
import { DIFFICULTY_OPTIONS, DOK_OPTIONS } from '../constants/form'
import StandardSet from '../../AssessmentPage/components/QuestionEditModal/common/StandardSet/StandardSet'
import { initialAlignmentData } from '../../AssessmentCreate/components/ImportTestFromDoc/constants'
import { standardsFields } from '../../AssessmentPage/VideoQuiz/constants'
import {
  getAIQuestionAssistanceState,
  generateAIQuestionMetadataAction,
  updateAISuggestedMetadataAction,
  resetAIQuestionMetadataStateAction,
} from '../ducks/questionAssistance'
import { setQuestionDataAction } from '../../QuestionEditor/ducks'
import {
  getStandardTooltipItems,
  hasQuestionContentForMetadata,
} from '../utils/AIQuestionAssistance'
import { getCurriculumsListSelector } from '../../src/selectors/dictionaries'
import { getItemIdSelector } from '../../sharedDucks/itemDetail'
import { isAIQuestionAssistanceRecommendationsEnabledSelector } from '../../src/selectors/user'

const QuestionMetadataSection = ({
  aiQuestionAssistanceState,
  currentQuestion,
  generateAIQuestionMetadata,
  onSaveSuggestedMetadata,
  allCurriculums,
  itemId,
  setQuestionData,
  updateAISuggestedMetadata,
  isAIQuestionAssistanceRecommendationsEnabled,
  resetAIQuestionMetadataState,
}) => {
  const { apiStatus, result, errorMessage } = aiQuestionAssistanceState || {}

  const [isStandardEditView, setIsStandardEditView] = useState(false)
  const [currentAlignment, setCurrentAlignment] = useState(initialAlignmentData)

  const isLoading = apiStatus === 'INITIATED'
  const hasError = apiStatus === 'FAILED'

  const hasQuestionContent = hasQuestionContentForMetadata(currentQuestion)

  const suggestedMetaData = result?.suggestedMetaData
  const {
    dok: suggestedDok = '',
    difficulty: suggestedDifficulty = '',
    standards: suggestedStandards = [],
    suggested_edits: suggestedEdits = {},
  } = suggestedMetaData || {}

  const {
    stimulus: suggestedStimulusEdit = '',
    options: suggestedOptionsEdits = [],
  } = suggestedEdits || {}

  const dok = suggestedDok || currentQuestion?.depthOfKnowledge || ''
  const difficulty =
    suggestedDifficulty || currentQuestion?.authorDifficulty || ''

  const handleDokChange = (value) => {
    if (currentQuestion) {
      setQuestionData({ ...currentQuestion, depthOfKnowledge: value })
    }
    updateAISuggestedMetadata({ dok: null })
  }

  const handleDifficultyChange = (value) => {
    if (currentQuestion) {
      setQuestionData({ ...currentQuestion, authorDifficulty: value })
    }
    updateAISuggestedMetadata({ difficulty: null })
  }

  const handleGenerateClick = () => {
    if (currentQuestion) {
      generateAIQuestionMetadata(currentQuestion)
    }
  }

  const difficultyOptions = DIFFICULTY_OPTIONS.filter(
    (option) => option.value !== ''
  )
  const dokOptions = DOK_OPTIONS.filter((option) => option.value !== '')

  const handleEditStandardClick = (value) => {
    setIsStandardEditView(value)
  }

  const updateAlignment = (_alignment) => {
    setCurrentAlignment(_alignment)
  }

  const renderRecommendations = () => {
    if (
      isEmpty(suggestedEdits) ||
      (isEmpty(suggestedStimulusEdit) && isEmpty(suggestedOptionsEdits))
    ) {
      return null
    }

    const hasStimulusEdit = suggestedStimulusEdit?.trim?.()?.length > 0
    const hasOptionsEdits =
      Array.isArray(suggestedOptionsEdits) && suggestedOptionsEdits.length > 0

    return (
      <>
        <StyledRecommendationsContainer>
          <StyledMetadataLabelContainer>
            <StyledMetadataLabel>Recommendations</StyledMetadataLabel>
            <Tooltip title="Improve readability of your question">
              <IconInfo
                width="14px"
                height="14px"
                fill="#919191"
                aria-hidden="true"
              />
            </Tooltip>
          </StyledMetadataLabelContainer>

          {hasStimulusEdit && (
            <StyledRecommendationItem>
              <StyledRecommendationLabel>Stimulus:</StyledRecommendationLabel>
              <StyledRecommendationText>
                {suggestedStimulusEdit}
              </StyledRecommendationText>
            </StyledRecommendationItem>
          )}

          {hasOptionsEdits && (
            <StyledRecommendationItem>
              <StyledRecommendationLabel>Options:</StyledRecommendationLabel>
              <StyledRecommendationList>
                {suggestedOptionsEdits.map((optionEdit, index) => (
                  <StyledRecommendationListItem key={index}>
                    {optionEdit}
                  </StyledRecommendationListItem>
                ))}
              </StyledRecommendationList>
            </StyledRecommendationItem>
          )}
        </StyledRecommendationsContainer>
        <StyledDivider />
      </>
    )
  }

  const renderStandards = () => {
    if (!suggestedStandards || suggestedStandards?.length === 0) {
      return null
    }

    const standard = suggestedStandards[0]

    const standardTooltipItems = getStandardTooltipItems(
      standard,
      allCurriculums
    )

    return (
      <StyledStandardContainer>
        <StyledMetadataLabelContainer>
          <StyledMetadataLabel>Standards</StyledMetadataLabel>
          <Tooltip
            title={
              <StyledStandardTooltipContainer>
                {standardTooltipItems.map((item) => (
                  <StyledStandardTooltipRow key={item.label}>
                    <StyledStandardTooltipLabel>
                      {item.label}:{' '}
                    </StyledStandardTooltipLabel>
                    {item.value}
                  </StyledStandardTooltipRow>
                ))}
              </StyledStandardTooltipContainer>
            }
          >
            <IconInfo
              width="14px"
              height="14px"
              fill="#919191"
              aria-hidden="true"
            />
          </Tooltip>
        </StyledMetadataLabelContainer>
        {isStandardEditView ? (
          <StandardSet
            alignment={currentAlignment}
            onUpdate={(data) => updateAlignment(data.alignment)}
            showIconBrowserBtn
            hideLabel
            standardsRequiredFields={[
              standardsFields.SUBJECT,
              standardsFields.GRADES,
              standardsFields.STANDARD_SET,
            ]}
            isDocBased
            isAIFlow
          />
        ) : (
          <>
            <StyledStandardIdentifierContainer>
              <StyledStandardIdentifier>
                {standard.identifier}
              </StyledStandardIdentifier>
              <StyledStandardEditContainer
                onClick={() => handleEditStandardClick(true)}
              >
                <IconEditMetaData aria-hidden="true" />
              </StyledStandardEditContainer>
            </StyledStandardIdentifierContainer>
            <StyledStandardDescription>
              {standard.description}
            </StyledStandardDescription>
          </>
        )}
      </StyledStandardContainer>
    )
  }

  const handleApplyChanges = () => {
    if (!suggestedStandards || suggestedStandards?.length === 0) {
      captureSentryException(
        new Error('No suggested standards available to apply'),
        {
          errorMessage:
            'AI Question Assistance - METADATA: No suggested standards found when applying AI metadata',
          itemId,
          questionId: currentQuestion?.id,
        }
      )

      notification({
        type: 'error',
        msg: 'An error occurred. Please try again later.',
      })

      resetAIQuestionMetadataState()
      return
    }

    if (onSaveSuggestedMetadata) {
      onSaveSuggestedMetadata()
    }
  }

  const renderMetadataContent = () => (
    <>
      <StyledMetadataSection>
        {isAIQuestionAssistanceRecommendationsEnabled &&
          renderRecommendations()}
        {renderStandards()}

        <StyledDivider />

        {dok && (
          <StyledMetadataItem>
            <StyledMetadataLabelContainer>
              <StyledMetadataLabel>Depth of Knowledge</StyledMetadataLabel>
            </StyledMetadataLabelContainer>
            <StyledSelect value={dok} onChange={handleDokChange}>
              {dokOptions.map((option) => (
                <Select.Option key={option.label} value={option.label}>
                  {option.label}
                </Select.Option>
              ))}
            </StyledSelect>
          </StyledMetadataItem>
        )}

        <StyledDivider />

        {difficulty && (
          <StyledMetadataItem>
            <StyledMetadataLabelContainer>
              <StyledMetadataLabel>Difficulty Level</StyledMetadataLabel>
            </StyledMetadataLabelContainer>
            <StyledSelect value={difficulty} onChange={handleDifficultyChange}>
              {difficultyOptions.map((option) => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </StyledSelect>
          </StyledMetadataItem>
        )}
      </StyledMetadataSection>

      <StyledApplyButton>
        <EduButton
          ml="0px"
          type="primary"
          onClick={handleApplyChanges}
          disabled={!currentQuestion}
        >
          Save Suggestions and Item
        </EduButton>
      </StyledApplyButton>
    </>
  )

  if (isLoading) {
    return (
      <StyledLoadingContainer>
        <StyledSpinnerWrapper>
          <StyledGradientLoader />
        </StyledSpinnerWrapper>
        <StyledThinkingText>
          Reading question to generate standards and other details
        </StyledThinkingText>
      </StyledLoadingContainer>
    )
  }

  if (apiStatus === 'SUCCESS' && !isEmpty(suggestedMetaData)) {
    return renderMetadataContent()
  }

  if (hasError) {
    return (
      <StyledErrorText type="danger">
        {errorMessage || 'Failed to generate metadata suggestions'}
      </StyledErrorText>
    )
  }

  return (
    <StyledEmptyStateContainer>
      <StyledEmptyStateText>
        After typing your question, click here to see AI suggested metadata
        (including standards).
      </StyledEmptyStateText>
      <StyledShowSuggestionsButton>
        <EduButton
          ml="0px"
          type="primary"
          onClick={handleGenerateClick}
          disabled={!hasQuestionContent}
        >
          Suggest Metadata
        </EduButton>
      </StyledShowSuggestionsButton>
    </StyledEmptyStateContainer>
  )
}

const enhance = compose(
  connect(
    (state) => ({
      aiQuestionAssistanceState: getAIQuestionAssistanceState(state),
      currentQuestion: getCurrentQuestionSelector(state),
      allCurriculums: getCurriculumsListSelector(state),
      itemId: getItemIdSelector(state),
      isAIQuestionAssistanceRecommendationsEnabled: isAIQuestionAssistanceRecommendationsEnabledSelector(
        state
      ),
    }),
    {
      generateAIQuestionMetadata: generateAIQuestionMetadataAction,
      setQuestionData: setQuestionDataAction,
      updateAISuggestedMetadata: updateAISuggestedMetadataAction,
      resetAIQuestionMetadataState: resetAIQuestionMetadataStateAction,
    }
  )
)
export default enhance(QuestionMetadataSection)
